import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Send, Bot, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

import { API_URL } from '../config';
const API = API_URL;

const AIAgent = ({ user, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add welcome message
    setMessages([{
      id: Date.now(),
      role: 'assistant',
      content: "Hello! I'm RedChannel AI Agent. I'm here to help you with:\n\n• Creating and managing posts\n• Managing admin roles\n• Updating app configuration\n• Analyzing data and suggesting improvements\n\nWhat would you like to do?",
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai-agent/chat`,
        { message: userMessage.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        action_info: response.data.action_info,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);

      // If action requires confirmation, store it
      if (response.data.action_info.requires_confirmation) {
        setPendingAction(response.data.action_info);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;

    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'system',
      content: `Action confirmed: ${pendingAction.action_type}. Please implement this action manually through the admin dashboard or provide the specific details needed.`,
      timestamp: new Date().toISOString()
    }]);

    setPendingAction(null);
    toast.success('Action noted. Please proceed with the operation manually.');
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    toast.info('Action cancelled');
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'refuse':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'analyze':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#050505', color: '#EDEDED' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: '#121212', borderBottom: '1px solid #2A2A2A' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => window.location.href = '/admin'}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Bot className="w-8 h-8" style={{ color: '#EE1C4E' }} />
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  AI Agent
                </h1>
                <p className="text-xs text-gray-400">Safe Controlled Operator</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-xs text-gray-400">Online</span>
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-4xl mx-auto w-full">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${message.role}`}
            >
              <div
                className={`max-w-[80%] rounded-xl p-4 ${
                  message.role === 'user'
                    ? 'bg-[#EE1C4E] text-white'
                    : message.role === 'system'
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#121212] border border-[#2A2A2A]'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="w-4 h-4" style={{ color: '#EE1C4E' }} />
                    <span className="text-xs font-bold" style={{ color: '#EE1C4E' }}>AI Agent</span>
                  </div>
                )}
                
                <div className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {message.content}
                </div>

                {message.action_info && (
                  <div className="mt-3 pt-3 border-t border-[#2A2A2A]">
                    <div className="flex items-center gap-2 text-xs">
                      {getActionIcon(message.action_info.action_type)}
                      <span className="text-gray-400">
                        Action: {message.action_info.action_type.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 mt-2">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#EE1C4E] rounded-full animate-pulse" />
                  <div className="w-2 h-2 bg-[#EE1C4E] rounded-full animate-pulse delay-100" />
                  <div className="w-2 h-2 bg-[#EE1C4E] rounded-full animate-pulse delay-200" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Pending Action Confirmation */}
      {pendingAction && (
        <div className="border-t border-[#2A2A2A] bg-[#1A1A1A] px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <div className="flex-1">
                <p className="text-sm font-bold">Action Requires Confirmation</p>
                <p className="text-xs text-gray-400">
                  This action will {pendingAction.action_type.replace('_', ' ')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelAction}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400"
                  data-testid="cancel-action"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmAction}
                  size="sm"
                  className="text-white"
                  style={{ background: '#EE1C4E' }}
                  data-testid="confirm-action"
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="sticky bottom-0 border-t border-[#2A2A2A]" style={{ background: '#121212' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask AI Agent anything..."
              className="flex-1 bg-[#1A1A1A] border-[#2A2A2A] text-white h-12"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-12 px-6 text-white font-bold rounded-full"
              style={{ background: '#EE1C4E' }}
              data-testid="send-message"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-2 text-center">
            AI Agent is a controlled operator. All actions require your confirmation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAgent;
